import { useState, useEffect, useCallback } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { openConversationInInbox } from '@/stores/conversation-store';

export interface IncomingPreviewData {
  title: string;
  body: string;
  conversationId?: string;
  timestamp: number;
}

/** Simple pub/sub so the notification store can push previews without React coupling */
type PreviewListener = (data: IncomingPreviewData) => void;
const listeners = new Set<PreviewListener>();

export function emitIncomingPreview(data: IncomingPreviewData) {
  listeners.forEach((fn) => fn(data));
}

function useIncomingPreviewListener(cb: PreviewListener) {
  useEffect(() => {
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, [cb]);
}

const AUTO_DISMISS_MS = 6000;

/**
 * Floating preview bar at the bottom of the screen.
 * Shown when a new notification arrives (via FCM) and the user is NOT on the inbox/chat view.
 * Clicking it navigates to that conversation if a conversationId is available.
 */
export function IncomingMessagePreview() {
  const [preview, setPreview] = useState<IncomingPreviewData | null>(null);
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    // Let the slide-out animation finish before clearing data
    setTimeout(() => setPreview(null), 300);
  }, []);

  const handleIncoming = useCallback((data: IncomingPreviewData) => {
    setPreview(data);
    setVisible(true);
  }, []);

  useIncomingPreviewListener(handleIncoming);

  // Auto-dismiss after a few seconds
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [visible, preview, dismiss]);

  const handleClick = useCallback(() => {
    if (!preview) return;
    if (preview.conversationId) {
      void openConversationInInbox(preview.conversationId);
    }
    dismiss();
  }, [preview, dismiss]);

  if (!preview) return null;

  const truncatedBody = preview.body.length > 120
    ? preview.body.slice(0, 120) + '...'
    : preview.body;

  return (
    <div
      className={cn(
        'fixed bottom-4 left-1/2 z-50 -translate-x-1/2 w-full max-w-md transition-all duration-300 ease-out',
        visible
          ? 'translate-y-0 opacity-100'
          : 'translate-y-4 opacity-0 pointer-events-none'
      )}
    >
      <div
        onClick={handleClick}
        className={cn(
          'relative mx-4 flex items-start gap-3 rounded-xl border border-border bg-background/95 backdrop-blur-sm p-3.5 shadow-lg transition-colors',
          preview.conversationId ? 'cursor-pointer hover:bg-accent/50' : ''
        )}
      >
        {/* Icon */}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <MessageCircle className="h-4 w-4 text-primary" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {preview.title}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {truncatedBody || 'New message'}
          </p>
        </div>

        {/* Dismiss */}
        <button
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          className="shrink-0 rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
